import { CashbackSummary } from "../../../../../services/accounts/types";
import { formatCurrency } from "../../../../../utils";
import { isInvestedCashback } from "../../../../../utils/cashback";
import { CashbackMode } from "../../../../../utils/types";
import { CashbackSide, CashbackSplitLine, WithdrawnFigures } from "./types";

export const CASH_NOTE =
  "Cashback on an expense is added to the balance when you save the expense. Use Add cashback for money you didn't track on an expense.";
export const SWITCHED_NOTE =
  "This account now invests its cashback. Cashback already added stays in the balance.";
export const INVESTED_NOTE = "Invested cashback is recorded on the buy.";
export const OFF_NOTE =
  "Cashback is off for this account. Earlier entries stay.";
export const PAID_ELSEWHERE_NOTE =
  "Some cashback from this account was paid to other accounts.";

export const cashbackSideOf = (
  { received, invested }: CashbackSummary,
  mode?: CashbackMode,
): CashbackSide | null => {
  if (received > 0 && invested > 0) {
    return {
      label: "Total cashback",
      amount: received + invested,
      caption: "All time, added to this balance or invested.",
    };
  }
  if (received > 0) {
    return {
      label: "Total cashback",
      amount: received,
      caption: "All time, added to this balance.",
    };
  }
  if (invested > 0 || isInvestedCashback(mode)) {
    return {
      label: "Total cashback invested",
      amount: invested,
      caption:
        "All time, through cashback buys. Your savings balance doesn't change.",
    };
  }
  return null;
};

export const cashbackSplitOf = ({
  received,
  receivedFromOthers,
  invested,
  paidElsewhere,
}: CashbackSummary): CashbackSplitLine[] => {
  const candidates: CashbackSplitLine[] = [
    {
      id: "received",
      label: "Added to balance",
      amount: received,
      note:
        receivedFromOthers > 0
          ? `Includes ${formatCurrency(receivedFromOthers)} from other accounts`
          : null,
    },
    { id: "invested", label: "Invested", amount: invested, note: null },
    {
      id: "paidElsewhere",
      label: "Paid to other accounts",
      amount: paidElsewhere,
      note: null,
    },
  ];
  const lines = candidates.filter((line) => line.amount > 0);
  return lines.length >= 2 ? lines : [];
};

const modeNotesOf = (received: number, mode?: CashbackMode): string[] => {
  if (mode === "CASH") {
    return [CASH_NOTE];
  }
  if (isInvestedCashback(mode)) {
    return received > 0 ? [SWITCHED_NOTE, INVESTED_NOTE] : [INVESTED_NOTE];
  }
  return [OFF_NOTE];
};

export const cashbackNotesOf = (
  { received, paidElsewhere }: CashbackSummary,
  mode?: CashbackMode,
): string[] => {
  const notes = modeNotesOf(received, mode);
  return paidElsewhere > 0 ? [...notes, PAID_ELSEWHERE_NOTE] : notes;
};

export const withdrawnNoteOf = ({
  interestWithdrawn,
  cashbackWithdrawn,
}: WithdrawnFigures): string | null => {
  const parts: string[] = [];
  if (interestWithdrawn > 0) {
    parts.push(`${formatCurrency(interestWithdrawn)} of interest`);
  }
  if (cashbackWithdrawn > 0) {
    parts.push(`${formatCurrency(cashbackWithdrawn)} of cashback`);
  }
  if (parts.length === 0) {
    return null;
  }
  return `Withdrawals went past your contributions, using ${parts.join(" and ")}. That's why net contributed isn't simply deposited minus withdrawn.`;
};
